// Small helpers ported from the mockup core.js (App.util, App.josa, App.cls, mdBlocks/mdPlain, firstSentence).

export type ClassValue = string | false | null | undefined | 0 | ClassValue[] | Record<string, boolean | null | undefined>;

/** Class names: strings, arrays and { name: condition } objects; falsy parts are dropped. */
export function cls(...parts: ClassValue[]): string {
  const out: string[] = [];
  const walk = (v: ClassValue) => {
    if (!v) return;
    if (typeof v === 'string') out.push(v);
    else if (Array.isArray(v)) v.forEach(walk);
    else for (const k of Object.keys(v)) if (v[k]) out.push(k);
  };
  parts.forEach(walk);
  return out.join(' ');
}

export const clamp = (v: number, a: number, b: number): number => Math.max(a, Math.min(b, v));

export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): ((...args: A) => void) & { cancel(): void } {
  let tm: ReturnType<typeof setTimeout> | undefined;
  const run = (...args: A) => {
    clearTimeout(tm);
    tm = setTimeout(() => fn(...args), ms);
  };
  return Object.assign(run, { cancel: () => clearTimeout(tm) });
}

// ── Korean search: initial consonants (초성) and space-insensitive matching ──
const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
export function chosung(str: string): string {
  let out = '';
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    out += c >= 0xac00 && c <= 0xd7a3 ? CHO[Math.floor((c - 0xac00) / 588)] : str.charAt(i);
  }
  return out;
}
export const norm = (str: unknown): string => String(str ?? '').toLowerCase().replace(/\s+/g, '');
export const isChosungQuery = (q: string): boolean => /^[ㄱ-ㅎ]+$/.test(q);

function mapIndex(text: string, compactIdx: number, len: number): { index: number; length: number } | null {
  let seen = 0;
  let start = -1;
  let end = -1;
  for (let i = 0; i < text.length; i++) {
    if (/\s/.test(text.charAt(i))) continue;
    if (seen === compactIdx) start = i;
    seen++;
    if (seen === compactIdx + len) {
      end = i + 1;
      break;
    }
  }
  return start < 0 ? null : { index: start, length: (end < 0 ? text.length : end) - start };
}

/** Find q in text: initial consonants compare by 초성, otherwise case- and space-insensitive. → { index, length } | null */
export function match(textIn: unknown, qIn: unknown): { index: number; length: number } | null {
  const text = String(textIn ?? '');
  const q = String(qIn ?? '').trim();
  if (!q) return { index: 0, length: 0 };
  const nq = norm(q);
  if (isChosungQuery(nq)) {
    const i0 = chosung(text).replace(/\s+/g, '').indexOf(nq);
    return i0 < 0 ? null : mapIndex(text, i0, nq.length);
  }
  const lower = text.toLowerCase();
  const i1 = lower.indexOf(q.toLowerCase());
  if (i1 >= 0) return { index: i1, length: q.length };
  const i2 = lower.replace(/\s+/g, '').indexOf(nq);
  return i2 >= 0 ? mapIndex(text, i2, nq.length) : null;
}

/**
 * Korean particle after a word: josa('직전 7일', '과', '와') → '과'. A trailing "(…)" note is skipped, and
 * '으로/로' takes '로' after ㄹ: josa('질문 글', '으로', '로') → '로'.
 */
export function josa(word: string, withBatchim: string, without: string): string {
  const c = String(word || '')
    .trim()
    .replace(/\s*\([^()]*\)$/, '')
    .trim()
    .replace(/[’'"”)\]]+$/, '')
    .slice(-1);
  const code = c.charCodeAt(0);
  const ro = withBatchim === '으로';
  if (code >= 0xac00 && code <= 0xd7a3) {
    const jong = (code - 0xac00) % 28;
    return jong ? (ro && jong === 8 ? without : withBatchim) : without;
  }
  if (/[0-9]$/.test(c)) {
    if (ro && '178'.includes(c)) return without;
    return '013678'.includes(c) ? withBatchim : without;
  }
  return without;
}

/** First sentence: cut only at . ! ? followed by a space (not inside parentheses or after e.g./i.e.). */
export function firstSentence(text: unknown): string {
  const str = String(text ?? '');
  const re = /[.!?](?=\s|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(str))) {
    const head = str.slice(0, m.index + 1);
    if (/(?:\be\.g|\bi\.e|\bvs|\betc|\bcf)\.$/i.test(head)) continue;
    if ((head.match(/\(/g) || []).length > (head.match(/\)/g) || []).length) continue;
    return head;
  }
  return str;
}

export type MdBlock = { type: 'p' | 'h'; text: string } | { type: 'ul' | 'ol'; items: string[] };

/** Light markup (glossary definitions, notice sections): paragraphs, '- ' lists, '1. ' lists, '# ' sub-headings. */
export function mdBlocks(src: unknown): MdBlock[] {
  const blocks: MdBlock[] = [];
  let cur: MdBlock | null = null;
  String(src ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .forEach((line) => {
      let m: RegExpExecArray | null;
      if (!line.trim()) {
        cur = null;
        return;
      }
      if ((m = /^\s*[-*•]\s+(.*)$/.exec(line))) {
        if (!cur || cur.type !== 'ul') blocks.push((cur = { type: 'ul', items: [] }));
        (cur as { items: string[] }).items.push(m[1] ?? '');
        return;
      }
      if ((m = /^\s*\d+[.)]\s+(.*)$/.exec(line))) {
        if (!cur || cur.type !== 'ol') blocks.push((cur = { type: 'ol', items: [] }));
        (cur as { items: string[] }).items.push(m[1] ?? '');
        return;
      }
      if ((m = /^\s*#{1,3}\s+(.*)$/.exec(line))) {
        blocks.push({ type: 'h', text: m[1] ?? '' });
        cur = null;
        return;
      }
      if (cur && cur.type === 'p') {
        cur.text += ' ' + line.trim();
        return;
      }
      blocks.push((cur = { type: 'p', text: line.trim() }));
    });
  return blocks;
}

/** One line without markup (tooltips, toggletips): list items joined with ' · ', **bold** and `code` marks removed. */
export function mdPlain(src: unknown): string {
  return mdBlocks(src)
    .map((b) => ('items' in b ? b.items.join(' · ') : b.text))
    .join(' ')
    .replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, '$1')
    .replace(/`([^`\n]+)`/g, '$1');
}

/** First sentence, plus the second when both fit in 220 characters (toggletip definition). */
export function defShort(text: string): string {
  const a = firstSentence(text);
  const rest = String(text).slice(a.length).trim();
  const b = rest ? firstSentence(rest) : '';
  return b && (a + ' ' + b).length <= 220 ? a + ' ' + b : a;
}

/** Copy text: Clipboard API in secure contexts, otherwise the hidden-textarea fallback. */
export function copyText(text: string): Promise<boolean> {
  const fallback = (): boolean => {
    try {
      const ta = document.createElement('textarea');
      ta.setAttribute('aria-hidden', 'true');
      ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  };
  if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(
      () => true,
      () => fallback(),
    );
  }
  return Promise.resolve(fallback());
}

export const cssEscape = (v: string): string => (typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(v) : String(v).replace(/"/g, '\\"'));

export const isMac = (): boolean => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

/** Local storage that never throws (private mode, blocked storage): keys are prefixed 'aiax.' like the mockup. */
export const store = {
  get(k: string): string | null {
    try {
      return window.localStorage.getItem('aiax.' + k);
    } catch {
      return null;
    }
  },
  set(k: string, v: string): void {
    try {
      window.localStorage.setItem('aiax.' + k, v);
    } catch {
      /* storage unavailable: keep it for this page only */
    }
  },
};
